import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

import { Button } from '@/shared/components/ui';
import ComingSoonDialog from '@/shared/components/ComingSoonDialog';
import {
  FeatureCard,
  FaqSection,
  SectionHeading,
  ServiceCard,
  StatCard,
  TestimonialsCarousel
} from '@/shared/components/SolarSections';
import {
  companyHighlights,
  companyStats,
  featuredServices,
  faqItems,
  solarBenefits,
  testimonials
} from '@/shared/utils/SolarData';

const HomePage = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden px-4 pt-8 pb-16 sm:px-6 lg:px-8 lg:pt-12 lg:pb-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-accent-primary/20 bg-accent-primary/10 px-4 py-2 text-sm font-medium text-accent-primary">
              <Sparkles className="h-4 w-4" />
              Premium solar solutions for homes, businesses, and industries
            </div>

            <div className="space-y-5">
              <h1 className="max-w-3xl text-4xl font-semibold leading-tight tracking-tight text-text-primary sm:text-5xl lg:text-6xl">
                Powering a Sustainable Future with Solar Energy
              </h1>
              <p className="max-w-2xl text-lg leading-8 text-text-secondary sm:text-xl">
                PS Group Solar Energy designs and delivers dependable solar systems that lower electricity costs, improve energy resilience, and support a cleaner future.
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Button size="lg" className="shadow-xl shadow-accent-primary/15" onClick={() => setDialogOpen(true)}>
                Get Free Consultation
              </Button>
              <Button variant="outline" size="lg" onClick={() => setDialogOpen(true)}>
                Request Quote
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-3xl border border-border-primary/70 bg-background-card/85 p-4">
                <p className="text-2xl font-semibold text-accent-primary">150+</p>
                <p className="mt-1 text-sm text-text-secondary">successful installations</p>
              </div>
              <div className="rounded-3xl border border-border-primary/70 bg-background-card/85 p-4">
                <p className="text-2xl font-semibold text-accent-secondary">35 MW+</p>
                <p className="mt-1 text-sm text-text-secondary">installed capacity</p>
              </div>
              <div className="rounded-3xl border border-border-primary/70 bg-background-card/85 p-4">
                <p className="text-2xl font-semibold text-accent-warning">98%</p>
                <p className="mt-1 text-sm text-text-secondary">client satisfaction</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-linear-to-br from-accent-warning/20 via-accent-primary/15 to-accent-secondary/20 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-border-primary/70 bg-linear-to-br from-[#06121f] via-[#0d2340] to-[#183d5d] p-6 shadow-2xl shadow-accent-secondary/10">
              <div className="absolute inset-0 opacity-30">
                <div className="absolute inset-x-6 top-6 h-20 rounded-3xl bg-linear-to-r from-accent-warning/70 to-transparent blur-2xl" />
                <div className="grid h-full grid-cols-3 gap-4 pt-20">
                  {Array.from({ length: 9 }).map((_, index) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-white/10 bg-linear-to-br from-slate-900 via-slate-800 to-slate-700 shadow-inner"
                    >
                      <div className="h-full rounded-2xl bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent_45%)]" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative space-y-5 rounded-[1.5rem] border border-white/10 bg-white/8 p-6 text-white backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white/70">Solar Performance</p>
                    <p className="mt-1 text-2xl font-semibold">Live generation snapshot</p>
                  </div>
                  <div className="rounded-full bg-accent-warning px-3 py-1 text-sm font-semibold text-slate-900">
                    +24% output
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-sm text-white/70">Today</p>
                    <p className="mt-2 text-3xl font-semibold">1.84 MWh</p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-sm text-white/70">CO2 saved</p>
                    <p className="mt-2 text-3xl font-semibold">12.7 tons</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <div className="flex items-center justify-between text-sm text-white/80">
                    <span>Panel utilization</span>
                    <span>91%</span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-white/10">
                    <div className="h-2 w-[91%] rounded-full bg-linear-to-r from-accent-warning to-accent-primary" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
          {companyHighlights.map(item => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="px-4 pt-12 pb-16 sm:px-6 lg:px-8 lg:pt-16 lg:pb-20">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
          <SectionHeading
            eyebrow="Why solar"
            title="A practical way to reduce costs and emissions"
            description="Solar energy helps households and businesses cut monthly bills, improve energy reliability, and contribute to a cleaner economy."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {solarBenefits.map(item => (
              <FeatureCard key={item.title} {...item} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pt-12 pb-16 sm:px-6 lg:px-8 lg:pt-16 lg:pb-20">
        <div className="mx-auto max-w-7xl space-y-10">
          <SectionHeading
            eyebrow="Statistics"
            title="Reliable outcomes that compound over time"
            description="Our approach combines engineering, premium hardware, and service discipline to deliver real energy value."
            align="center"
          />
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {companyStats.map(stat => (
              <StatCard key={stat.label} {...stat} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pt-12 pb-16 sm:px-6 lg:px-8 lg:pt-16 lg:pb-20">
        <div className="mx-auto max-w-7xl space-y-10">
          <SectionHeading
            eyebrow="Services"
            title="Featured solar services"
            description="From feasibility studies to maintenance programs, we support the full lifecycle of your solar investment."
            align="center"
          />
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {featuredServices.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pt-12 pb-16 sm:px-6 lg:px-8 lg:pt-16 lg:pb-20">
        <div className="mx-auto max-w-7xl space-y-10">
          <SectionHeading
            eyebrow="Testimonials"
            title="Trusted by residential and business clients"
            description="A quick look at the type of experience customers report after working with our team."
            align="center"
          />
          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>

      <section className="px-4 pt-12 pb-16 sm:px-6 lg:px-8 lg:pt-16 lg:pb-20">
        <div className="mx-auto max-w-7xl space-y-10">
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently asked questions"
            description="Common questions about solar installation, approvals, maintenance, and project planning."
            align="center"
          />
          <FaqSection items={faqItems} />
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-border-primary/70 bg-linear-to-r from-accent-secondary to-accent-primary px-6 py-10 text-white shadow-2xl shadow-accent-secondary/15 sm:px-8 lg:px-12">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-white/80">
                Ready to get started?
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                Book a consultation and get a tailored solar proposal.
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/85 sm:text-base">
                We&apos;ll help you understand system sizing, savings, subsidies, and the best path for your property or business.
              </p>
            </div>
            <Button size="lg" variant="outline" className="border-white/20 bg-white text-slate-900 hover:bg-white/90" onClick={() => setDialogOpen(true)}>
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <ComingSoonDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
};

export default HomePage;